<script setup lang="ts">
import { Eye as EyeIcon, EyeOff as EyeOffIcon, UserPlus as UserPlusIcon } from '@lucide/vue'
import { storeToRefs } from 'pinia'
import { useField, useForm } from 'vee-validate'
import { ref, onMounted, nextTick, watch } from 'vue'

import BaseCheckbox from '@/components/ui/BaseCheckbox.vue'
import BaseSpinner from '@/components/ui/BaseSpinner.vue'
import CONFIG from '@/services/configService'
import {
  CURRENT_TERMS_VERSION,
  PRIVACY_POLICY_PATH,
  TERMS_OF_USE_PATH,
} from '@/services/legalDocumentService'
import { useAuthStore } from '@/stores/authStore'
import { validatePassword, createConfirmPasswordValidator } from '@/utils/passwordValidation'
import { validateProfileName } from '@/utils/profileValidation'

interface Emits {
  success: []
  switchToLogin: []
}

interface Props {
  initialInviteCode?: string
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Auth store
const authStore = useAuthStore()
const { isLoading, error } = storeToRefs(authStore)

// Password visibility
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const inviteOnlySignupEnabled = ref(CONFIG.INVITE_ONLY_SIGNUP_ENABLED)

// Refs for focus management
const emailInputRef = ref<HTMLInputElement | null>(null)
const inviteCodeInputRef = ref<HTMLInputElement | null>(null)

// Form setup
const { handleSubmit, resetForm } = useForm()

// Email validation function
const validateEmail = (value: string) => {
  if (!value) {
    return 'Email is required'
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(value)) {
    return 'Please enter a valid email'
  }
  return true
}

// Profile name validation function
const validateName = validateProfileName

// Confirm password validation function
const validateConfirmPassword = createConfirmPasswordValidator(() => password.value)

const validateInviteCode = (value?: string) => {
  if (!inviteOnlySignupEnabled.value) {
    return true
  }
  if (!value || !value.trim()) {
    return 'Invite code is required'
  }
  return true
}

const validateTermsAcceptance = (value: boolean) =>
  value === true || 'You must agree to the Terms of Use to create an account'

// Form fields
const { value: email, errorMessage: emailError, meta: emailMeta } = useField('email', validateEmail)

const { value: name, errorMessage: nameError, meta: nameMeta } = useField('name', validateName)

const {
  value: password,
  errorMessage: passwordError,
  meta: passwordMeta,
} = useField('password', validatePassword)

const {
  value: confirmPassword,
  errorMessage: confirmPasswordError,
  meta: confirmPasswordMeta,
} = useField('confirmPassword', validateConfirmPassword)

const {
  value: inviteCode,
  errorMessage: inviteCodeError,
  meta: inviteCodeMeta,
} = useField('inviteCode', validateInviteCode)

const {
  value: acceptedTerms,
  errorMessage: acceptedTermsError,
  handleBlur: handleAcceptedTermsBlur,
} = useField<boolean>('acceptedTerms', validateTermsAcceptance, {
  initialValue: false,
})

watch(
  () => props.initialInviteCode,
  (nextInviteCode) => {
    const normalizedInviteCode = nextInviteCode?.trim().toUpperCase()
    if (normalizedInviteCode) {
      inviteCode.value = normalizedInviteCode
      nextTick(() => {
        emailInputRef.value?.focus()
      })
    }
  },
  { immediate: true },
)

const hasInitialInviteCode = () => Boolean(props.initialInviteCode?.trim())

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const toggleConfirmPasswordVisibility = () => {
  showConfirmPassword.value = !showConfirmPassword.value
}

// Handle form submission
const onSubmit = handleSubmit(async (values) => {
  authStore.clearError()

  const success = await authStore.signup({
    email: values.email.trim(),
    password: values.password,
    name: values.name.trim(),
    inviteCode: values.inviteCode?.trim() || undefined,
    acceptedTerms: values.acceptedTerms,
    termsVersion: CURRENT_TERMS_VERSION,
  })

  if (success) {
    resetForm()
    emit('success')
  }
})

// Switch to login
const handleSwitchToLogin = () => {
  resetForm()
  authStore.clearError()
  emit('switchToLogin')
}

// Clear errors and focus when component mounts
onMounted(() => {
  authStore.clearError()

  // Focus the first input after component is mounted
  nextTick(() => {
    if (inviteOnlySignupEnabled.value && !hasInitialInviteCode() && inviteCodeInputRef.value) {
      inviteCodeInputRef.value.focus()
    } else if (emailInputRef.value) {
      emailInputRef.value.focus()
    }
  })
})
</script>

<template>
  <!-- Add live region for form status announcements -->
  <div id="signup-status" class="sr-only" aria-live="polite" aria-atomic="true">
    <span v-if="isLoading">Creating account, please wait...</span>
    <span v-else-if="error">{{ error }}</span>
  </div>

  <form @submit.prevent="onSubmit" class="space-y-4" novalidate>
    <div
      v-if="inviteOnlySignupEnabled"
      class="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-4"
    >
      <label for="signup-invite-code" class="mb-2 block text-sm font-semibold text-emerald-900">
        Invite Code <span class="text-red-500" aria-label="required">*</span>
      </label>
      <input
        id="signup-invite-code"
        ref="inviteCodeInputRef"
        v-model="inviteCode"
        type="text"
        autocomplete="off"
        required
        :class="[
          'w-full',
          'rounded-md',
          'border',
          'bg-white',
          'px-3',
          'py-2.5',
          'font-mono',
          'text-sm',
          'font-semibold',
          'tracking-[0.18em]',
          'uppercase',
          'focus:outline-none',
          'focus:ring-2',
          'transition-colors',
          {
            'border-red-300 focus:ring-red-500': inviteCodeError,
            'border-green-300 focus:ring-green-500': inviteCodeMeta.valid && inviteCodeMeta.touched,
            'border-emerald-300 focus:ring-emerald-500':
              !inviteCodeError && (!inviteCodeMeta.valid || !inviteCodeMeta.touched),
          },
        ]"
        placeholder="ENTER YOUR INVITE CODE"
        :disabled="isLoading"
        :aria-invalid="inviteCodeError ? 'true' : 'false'"
        :aria-describedby="inviteCodeError ? 'signup-invite-code-error' : 'signup-invite-code-help'"
      />
      <div
        v-if="!inviteCodeError"
        id="signup-invite-code-help"
        class="mt-2 text-xs text-emerald-800"
      >
        routecamp is currently invite-only. This code unlocks account creation.
      </div>
      <div
        v-if="inviteCodeError"
        id="signup-invite-code-error"
        class="mt-2 text-sm text-red-600"
        role="alert"
      >
        {{ inviteCodeError }}
      </div>
    </div>

    <!-- Email field -->
    <div>
      <label for="signup-email" class="block text-sm font-medium text-gray-700 mb-2">
        Email Address <span class="text-red-500" aria-label="required">*</span>
      </label>
      <input
        id="signup-email"
        ref="emailInputRef"
        v-model="email"
        type="email"
        autocomplete="email"
        required
        :class="[
          // Sizing
          'w-full',
          // Spacing
          'px-3',
          'py-2',
          // Appearance
          'border',
          'rounded-md',
          // Focus states
          'focus:outline-none',
          'focus:ring-2',
          // Transition
          'transition-colors',
          // Conditional states
          {
            'border-red-300 focus:ring-red-500': emailError,
            'border-green-300 focus:ring-green-500': emailMeta.valid && emailMeta.touched,
            'border-gray-300 focus:ring-primary-500':
              !emailError && (!emailMeta.valid || !emailMeta.touched),
          },
        ]"
        placeholder="Enter your email address"
        :disabled="isLoading"
        :aria-invalid="emailError ? 'true' : 'false'"
        :aria-describedby="emailError ? 'signup-email-error' : 'signup-email-help'"
      />
      <div v-if="!emailError" id="signup-email-help" class="sr-only">
        Enter your email address to create an account
      </div>
      <div v-if="emailError" id="signup-email-error" class="mt-1 text-sm text-red-600" role="alert">
        {{ emailError }}
      </div>
    </div>

    <!-- Profile Name field -->
    <div>
      <label for="signup-name" class="block text-sm font-medium text-gray-700 mb-2">
        Profile Name (visible to others) <span class="text-red-500" aria-label="required">*</span>
      </label>
      <input
        id="signup-name"
        v-model="name"
        type="text"
        autocomplete="name"
        required
        :class="[
          // Sizing
          'w-full',
          // Spacing
          'px-3',
          'py-2',
          // Appearance
          'border',
          'rounded-md',
          // Focus states
          'focus:outline-none',
          'focus:ring-2',
          // Transition
          'transition-colors',
          // Conditional states
          {
            'border-red-300 focus:ring-red-500': nameError,
            'border-green-300 focus:ring-green-500': nameMeta.valid && nameMeta.touched,
            'border-gray-300 focus:ring-primary-500':
              !nameError && (!nameMeta.valid || !nameMeta.touched),
          },
        ]"
        placeholder="Enter your profile name"
        :disabled="isLoading"
        :aria-invalid="nameError ? 'true' : 'false'"
        :aria-describedby="nameError ? 'signup-name-error' : 'signup-name-help'"
      />
      <div v-if="!nameError" id="signup-name-help" class="sr-only">
        Enter your profile name (2-50 characters)
      </div>
      <div v-if="nameError" id="signup-name-error" class="mt-1 text-sm text-red-600" role="alert">
        {{ nameError }}
      </div>
    </div>

    <!-- Password field -->
    <div>
      <label for="signup-password" class="block text-sm font-medium text-gray-700 mb-2">
        Password <span class="text-red-500" aria-label="required">*</span>
      </label>
      <div class="relative">
        <input
          id="signup-password"
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="new-password"
          required
          :class="[
            // Sizing
            'w-full',
            // Spacing
            'px-3',
            'py-2',
            'pr-10',
            // Appearance
            'border',
            'rounded-md',
            // Focus states
            'focus:outline-none',
            'focus:ring-2',
            // Transition
            'transition-colors',
            // Conditional states
            {
              'border-red-300 focus:ring-red-500': passwordError,
              'border-green-300 focus:ring-green-500': passwordMeta.valid && passwordMeta.touched,
              'border-gray-300 focus:ring-primary-500':
                !passwordError && (!passwordMeta.valid || !passwordMeta.touched),
            },
          ]"
          placeholder="Enter your password"
          :disabled="isLoading"
          :aria-invalid="passwordError ? 'true' : 'false'"
          :aria-describedby="passwordError ? 'signup-password-error' : 'signup-password-help'"
        />
        <button
          type="button"
          @click="togglePasswordVisibility"
          :class="[
            // Position & layout
            'absolute',
            'right-3',
            'top-1/2',
            'transform',
            '-translate-y-1/2',
            'p-1',
            // Colors & interaction
            'text-gray-400',
            'hover:text-gray-600',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            'focus:ring-offset-2',
            // Shape
            'rounded-md',
          ]"
          :aria-label="showPassword ? 'Hide password' : 'Show password'"
          :aria-pressed="showPassword"
          :disabled="isLoading"
          tabindex="0"
        >
          <EyeOffIcon v-if="showPassword" class="w-5 h-5" />
          <EyeIcon v-else class="w-5 h-5" />
        </button>
      </div>
      <div v-if="!passwordError" id="signup-password-help" class="mt-1 text-xs text-gray-500">
        Password must be at least 8 characters long
      </div>
      <div
        v-if="passwordError"
        id="signup-password-error"
        class="mt-1 text-sm text-red-600"
        role="alert"
      >
        {{ passwordError }}
      </div>
    </div>

    <!-- Confirm Password field -->
    <div>
      <label for="signup-confirm-password" class="block text-sm font-medium text-gray-700 mb-2">
        Confirm Password <span class="text-red-500" aria-label="required">*</span>
      </label>
      <div class="relative">
        <input
          id="signup-confirm-password"
          v-model="confirmPassword"
          :type="showConfirmPassword ? 'text' : 'password'"
          autocomplete="new-password"
          required
          :class="[
            // Sizing
            'w-full',
            // Spacing
            'px-3',
            'py-2',
            'pr-10',
            // Appearance
            'border',
            'rounded-md',
            // Focus states
            'focus:outline-none',
            'focus:ring-2',
            // Transition
            'transition-colors',
            // Conditional states
            {
              'border-red-300 focus:ring-red-500': confirmPasswordError,
              'border-green-300 focus:ring-green-500':
                confirmPasswordMeta.valid && confirmPasswordMeta.touched,
              'border-gray-300 focus:ring-primary-500':
                !confirmPasswordError &&
                (!confirmPasswordMeta.valid || !confirmPasswordMeta.touched),
            },
          ]"
          placeholder="Confirm your password"
          :disabled="isLoading"
          :aria-invalid="confirmPasswordError ? 'true' : 'false'"
          :aria-describedby="
            confirmPasswordError ? 'signup-confirm-password-error' : 'signup-confirm-password-help'
          "
        />
        <button
          type="button"
          @click="toggleConfirmPasswordVisibility"
          :class="[
            // Position & layout
            'absolute',
            'right-3',
            'top-1/2',
            'transform',
            '-translate-y-1/2',
            'p-1',
            // Colors & interaction
            'text-gray-400',
            'hover:text-gray-600',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            'focus:ring-offset-2',
            // Shape
            'rounded-md',
          ]"
          :aria-label="showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'"
          :aria-pressed="showConfirmPassword"
          :disabled="isLoading"
          tabindex="0"
        >
          <EyeOffIcon v-if="showConfirmPassword" class="w-5 h-5" />
          <EyeIcon v-else class="w-5 h-5" />
        </button>
      </div>
      <div v-if="!confirmPasswordError" id="signup-confirm-password-help" class="sr-only">
        Re-enter your password to confirm
      </div>
      <div
        v-if="confirmPasswordError"
        id="signup-confirm-password-error"
        class="mt-1 text-sm text-red-600"
        role="alert"
      >
        {{ confirmPasswordError }}
      </div>
    </div>

    <div>
      <label class="flex items-start gap-3 text-sm text-gray-700">
        <BaseCheckbox
          v-model="acceptedTerms"
          class="mt-0.5 shrink-0"
          :disabled="isLoading"
          :aria-invalid="acceptedTermsError ? 'true' : 'false'"
          :aria-describedby="acceptedTermsError ? 'signup-terms-error' : 'signup-terms-help'"
          @blur="handleAcceptedTermsBlur"
        />
        <span>
          I confirm that I am at least 16 and have parent or guardian permission where required. I
          agree to the
          <a
            :href="TERMS_OF_USE_PATH"
            target="_blank"
            rel="noopener noreferrer"
            class="font-medium text-primary-600 hover:text-primary-800 hover:underline"
          >
            Terms of Use
          </a>
          and acknowledge the
          <a
            :href="PRIVACY_POLICY_PATH"
            target="_blank"
            rel="noopener noreferrer"
            class="font-medium text-primary-600 hover:text-primary-800 hover:underline"
          >
            Privacy Policy</a
          >.
        </span>
      </label>
      <p v-if="!acceptedTermsError" id="signup-terms-help" class="sr-only">
        Agreement to the Terms of Use is required to create an account
      </p>
      <p v-else id="signup-terms-error" class="mt-1 text-sm text-red-600" role="alert">
        {{ acceptedTermsError }}
      </p>
    </div>

    <!-- Error message -->
    <div
      v-if="error"
      class="p-3 bg-red-50 border border-red-200 rounded-md"
      role="alert"
      aria-live="polite"
    >
      <p class="text-sm text-red-600">{{ error }}</p>
    </div>

    <!-- Submit button -->
    <button
      type="submit"
      :disabled="isLoading"
      :aria-describedby="isLoading ? 'signup-loading-status' : undefined"
      :class="[
        // Layout & sizing
        'w-full',
        'flex',
        'items-center',
        'justify-center',
        'px-4',
        'py-2',
        // Colors & states
        'bg-primary-600',
        'hover:bg-primary-700',
        'disabled:bg-primary-400',
        'text-white',
        // Typography & effects
        'font-medium',
        'rounded-md',
        'transition-colors',
        // Focus & interaction
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-primary-500',
        'focus:ring-offset-2',
        'hover:cursor-pointer',
      ]"
    >
      <UserPlusIcon v-if="!isLoading" class="w-4 h-4 mr-2" aria-hidden="true" />
      <BaseSpinner v-else class="mr-2" aria-hidden="true" />
      {{ isLoading ? 'Creating Account...' : 'Create Account' }}
    </button>
    <div v-if="isLoading" id="signup-loading-status" class="sr-only">
      Please wait while we create your account
    </div>

    <!-- Footer link -->
    <div class="text-center">
      <div class="flex items-center justify-center space-x-1 text-sm">
        <span class="text-gray-600">Already have an account?</span>
        <button
          type="button"
          @click="handleSwitchToLogin"
          :class="[
            // Colors & interaction
            'text-primary-600',
            'hover:text-primary-800',
            'font-medium',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            'focus:ring-offset-2',
            // Shape
            'rounded-sm',
          ]"
          :disabled="isLoading"
          aria-describedby="login-help"
        >
          Log in
        </button>
      </div>
      <div id="login-help" class="sr-only">Click to log in with an existing account</div>
    </div>
  </form>
</template>
